// Server-side enforcement + audit trail for the single-account duplicate
// submission testing exception. The UI button is only a convenience: this
// handler is the authorization boundary. Forged client flags are ignored —
// eligibility is derived solely from the verified session claims.

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { isDuplicateBypassAuthorizedClaims } from "@/lib/duplicate-bypass";

export class DuplicateBypassNotAuthorizedError extends Error {
  constructor() {
    super("Not authorized to bypass duplicate invoice detection.");
    this.name = "DuplicateBypassNotAuthorizedError";
  }
}

interface BypassInput {
  fileHash: string;
  existingReviewId: string;
  existingFileName?: string | null;
  existingUploadedAt?: string | null;
}

interface AuditInput extends BypassInput {
  newReviewId: string;
  newFileNumber: string;
  existingFileNumber?: string | null;
}

function claimEmail(claims: Record<string, unknown>): string | null {
  const email = claims["email"];
  return typeof email === "string" ? email : null;
}

function authorized(claims: unknown): boolean {
  return isDuplicateBypassAuthorizedClaims(claims as Record<string, unknown>);
}

/**
 * Authorize a duplicate re-submission. Returns { allowed: true } only for the
 * allowlisted testing account; every other authenticated user gets an
 * authorization error and the normal duplicate-blocking behavior.
 */
export const authorizeDuplicateInvoiceBypass = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: BypassInput) => {
    if (!data || typeof data.fileHash !== "string" || !data.fileHash) {
      throw new Error("fileHash is required");
    }
    if (typeof data.existingReviewId !== "string" || !data.existingReviewId) {
      throw new Error("existingReviewId is required");
    }
    return data;
  })
  .handler(async ({ data, context }) => {
    if (!authorized(context.claims)) {
      const { logSecurityEvent } = await import("@/lib/security-log.server");
      await logSecurityEvent({
        eventType: "duplicate_bypass_denied",
        severity: "warning",
        userId: context.userId,
        metadata: {
          existing_review_id: data.existingReviewId,
          duplicate_match_basis: "file_sha256",
        },
      });
      throw new DuplicateBypassNotAuthorizedError();
    }
    return { allowed: true as const };
  });

/**
 * Record the audit trail for an approved bypass. Re-verifies authorization —
 * never trust the caller to have gone through the authorize step.
 */
export const recordDuplicateInvoiceBypass = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: AuditInput) => {
    if (!data || typeof data.newReviewId !== "string" || !data.newReviewId) {
      throw new Error("newReviewId is required");
    }
    if (typeof data.existingReviewId !== "string" || !data.existingReviewId) {
      throw new Error("existingReviewId is required");
    }
    return data;
  })
  .handler(async ({ data, context }) => {
    if (!authorized(context.claims)) {
      throw new DuplicateBypassNotAuthorizedError();
    }
    const email = claimEmail(context.claims as Record<string, unknown>);

    const timestamp = new Date().toISOString();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Customer-visible timeline entry stays deliberately terse.
    await supabaseAdmin.from("timeline_events").insert({
      review_id: data.newReviewId,
      event_type: "duplicate_bypass",
      title: "Duplicate invoice resubmitted for authorized testing",
      description: `Duplicate warning acknowledged and bypassed. Original review ${
        data.existingFileNumber ?? data.existingReviewId
      } is unchanged.`,
      actor_id: context.userId,
      visible_to_customer: false,
    });

    const { logSecurityEvent } = await import("@/lib/security-log.server");
    await logSecurityEvent({
      eventType: "duplicate_bypass_approved",
      severity: "info",
      userId: context.userId,
      metadata: {
        authenticated_email: email,
        original_review_id: data.existingReviewId,
        original_file_number: data.existingFileNumber ?? null,
        original_file_name: data.existingFileName ?? null,
        original_uploaded_at: data.existingUploadedAt ?? null,
        new_review_id: data.newReviewId,
        new_file_number: data.newFileNumber,
        duplicate_match_basis: "file_sha256",
        file_hash: data.fileHash,
        testing_bypass: true,
        recorded_at: timestamp,
      },
    });

    return { recorded: true as const, recordedAt: timestamp };
  });
